import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import predeploymentHeroImage from 'assets/img/new_customer_landing/hero/pre-deployment.webp?&imagetools';
import { clsx } from 'clsx/lite';
import { BorderlessPanel, CardButton, Picture } from 'components';
import sharedPageStyles from 'pages/sharedPage.module.css';
import { useCallback, useMemo, useRef, useState } from 'react';
import { OnButtonClick } from 'types/common';
import { useDebounceCallback } from 'utils/hooks';
import styles from './LandingEmpty.module.css';

interface LandingEmptyProps {
  className?: string;
  firstName?: string;
  hasPredeploymentTesting: boolean;
  onActivate: () => void;
  canActivate: boolean;
  canActivateErrorMessage?: string;
  isManaged?: boolean;
  orgId?: number;
  orgName?: string;
  email?: string; // Unused for now
  onButtonClick: OnButtonClick;
}

const LandingEmpty: React.FC<LandingEmptyProps> = ({
  className,
  firstName,
  hasPredeploymentTesting = false,
  onActivate,
  canActivate,
  canActivateErrorMessage,
  isManaged = false,
  orgId,
  orgName,
  onButtonClick,
}) => {
  const [shakingError, setShakingError] = useState(false);
  const errorCalloutRef = useRef<HTMLDivElement | null>(null);
  const clearShakeEffect = useCallback(() => {
    if (shakingError) {
      setShakingError(false);
    }
  }, [shakingError]);
  useDebounceCallback(clearShakeEffect, 250);

  const handleActivate = useCallback(() => {
    if (!canActivate) {
      if (errorCalloutRef.current) {
        // This will currently only show visible focus on Firefox
        errorCalloutRef.current.focus({ preventScroll: false, focusVisible: true } as any);
        setShakingError(true);
      }
      return;
    }

    onActivate();
  }, [canActivate, onActivate]);

  const gettingStartedPanel = useMemo(() => {
    if (hasPredeploymentTesting) {
      return (
        <Panel className={styles.start} header="You’re set up with pre-deployment testing">
          <div className={styles.educationPanelContentsWrapper}>
            <div className={styles.educationPanelContents}>
              <p>Pre-deployment testing automates your activation workflows.</p>
              <p>
                Connectivity and billing enable automatically once your SIM uses data, whether
                that’s at your end customer or your manufacturer. Once a SIM reaches the end of its
                testing period, uses 100 KB of data, or sends 10 outbound SMS messages, it will
                automatically activate.
              </p>
              <p>Once your first SIMs are set up, you can view them here in your dashboard.</p>

              <Button
                variant="secondary"
                iconStart="StatusTesting"
                onClick={() =>
                  onButtonClick(
                    'Learn more about pre-deployment testing',
                    'https://support.hologram.io/hc/en-us/articles/360035696873-Pre-deployment-testing-with-Preflight'
                  )
                }
              >
                Learn more about pre-deployment testing
              </Button>
            </div>
            <div className={styles.educationPanelContentsRight}>
              <Picture image={predeploymentHeroImage} setDimensions={false} alt="" />
            </div>
          </div>
        </Panel>
      );
    }

    if (!isManaged) {
      return (
        <Panel
          className={styles.start}
          header="Get a free Hologram SIM"
          footer={
            <Button
              variant="secondary"
              iconStart="Chat"
              onClick={() =>
                onButtonClick('Contact sales', 'https://www.hologram.io/contact-sales/')
              }
            >
              Contact sales
            </Button>
          }
          footerActions={
            <>
              <Button
                variant="secondary"
                iconStart="Cart"
                onClick={() => onButtonClick('Hologram Store', 'https://store.hologram.io/')}
              >
                Hologram Store
              </Button>
              <Button
                iconStart="SIM"
                onClick={() =>
                  onButtonClick('Get a free SIM', 'https://store.hologram.io/discount/freepilotsim')
                }
              >
                Get a free SIM
              </Button>
            </>
          }
        >
          <div className={styles.educationPanelContents}>
            <p>
              Get online this week — we’ll send you a free SIM card to get started! If you’re ready
              to test with a larger deployment, grab our 100 SIM pack.
            </p>
            <p>No matter how you start, we’re here every step of the way.</p>
          </div>
        </Panel>
      );
    }

    return null;
  }, [hasPredeploymentTesting, isManaged, onButtonClick]);

  const orderSimsLink = useMemo(() => {
    const orderSimsSubject = encodeURIComponent(`New SIM order - ${orgName} (${orgId})`);
    return isManaged
      ? `mailto:support@hologram.io?subject=${orderSimsSubject}`
      : 'https://store.hologram.io/';
  }, [isManaged, orgId, orgName]);

  return (
    <div
      className={clsx(
        sharedPageStyles.pageWrapper,
        sharedPageStyles.pageWrapperScrollable,
        styles.wrapper,
        className
      )}
    >
      <BorderlessPanel
        headerContents={
          <div className={styles.headerContents}>
            <h1 className={styles.title}>
              {firstName ? `Welcome to Hologram, ${firstName}` : 'Welcome to Hologram!'}
            </h1>
            <p className={styles.description}>
              You’re ready to get connected with a free Hologram SIM card — or activate a SIM you
              already have. Check out the resources below for additional reference.
            </p>
            <div className={styles.actions}>
              <Button onClick={handleActivate} iconStart="Lightning">
                Activate your first SIM
              </Button>
            </div>
          </div>
        }
      >
        <div className={styles.panelContents}>
          {!canActivate && (
            <div className={styles.errorBanner}>
              <BannerNotification
                ref={errorCalloutRef}
                className={clsx(shakingError && styles.errorCalloutShake)}
                variant="invalid"
              >
                {canActivateErrorMessage}
              </BannerNotification>
            </div>
          )}
          <div className={styles.order}>
            <CardButton
              variant={isManaged ? 'form' : 'link'}
              descriptiveIcon="Cart"
              descriptiveIconFill="DdsColorPaletteTeal40"
              descriptiveIconHoverFill="DdsColorPaletteTeal30"
              backgroundGradient="default"
              descriptivePicture="hyper"
              onClick={() => onButtonClick('Order a Hologram SIM', orderSimsLink)}
            >
              Order a Hologram SIM
            </CardButton>
          </div>
          <div className={styles.activate}>
            <CardButton
              descriptiveIcon="Lightning"
              descriptiveIconFill="DdsColorPaletteTeal40"
              descriptiveIconHoverFill="DdsColorPaletteTeal30"
              backgroundGradient="b"
              descriptivePicture="sim"
              onClick={handleActivate}
            >
              Activate a Hologram SIM
            </CardButton>
          </div>
          {gettingStartedPanel}
          <Panel
            className={styles.tools}
            header="Tools and guides for developers"
            footerActions={
              <>
                <Button
                  variant="secondary"
                  onClick={() =>
                    onButtonClick('Explore the community', 'https://community.hologram.io/')
                  }
                >
                  Explore the community
                </Button>
                <Button
                  variant="secondary"
                  onClick={() =>
                    onButtonClick(
                      'Read FAQs',
                      'https://support.hologram.io/hc/en-us/sections/360005468613-Frequently-Asked-Questions'
                    )
                  }
                >
                  Read FAQs
                </Button>
                <Button
                  variant="secondary"
                  iconStart="BookOpen"
                  onClick={() =>
                    onButtonClick(
                      'Review our documentation',
                      'https://support.hologram.io/hc/en-us'
                    )
                  }
                >
                  Review our documentation
                </Button>
              </>
            }
          >
            <div className={styles.educationPanelContents}>
              <p>Learn more about connecting your devices and our REST API and SDKs.</p>
            </div>
          </Panel>
        </div>
      </BorderlessPanel>
    </div>
  );
};

export default LandingEmpty;
